import { cn } from "@workspace/web-ui/lib/utils";
import { Calendar, CircleCheck, Clock, Info, LucideIcon, Pill, Stethoscope } from "lucide-react";
import { useState, useEffect } from "react";
import { EncounterTagStatusType, TAG_STYLES } from "@workspace/web-ui/lib/encounter";
// import { MedicalRefillsComponent } from "~/features/dashboard/components/refills";
import { FormattedEncounter } from "../../../encounters/types";
import { ConsultationModal } from "../drawers/consultation-modal";
import { AppointmentModal } from "../drawers/appointment-modal";
import { formatTimeAgo } from "../../../encounters/components/urgentcare-card";
import { getRouteApi } from "@tanstack/react-router";
import { MedicalRefillsSheet } from "~/features/dashboard/components/refills/refill-sheet";
import { Image } from "@workspace/web-ui/components/image";

export interface UpcomingEncounterCardProps {
  item: FormattedEncounter;
  program: string
}

const CARD_COLORS = {
  medication: "bg-[#00C781]",
  appointment: "bg-[#4272DD]",
  consultation: "bg-[#7B238D]"
} as const;

const AppointmentStatus = ({ status, tag, item }: { status: string, tag: string, item: FormattedEncounter }) => {
  // Only show action/resolved tags for appointments that have started or passed
  const hasStartedOrPassed = ["happening", "past", "completed", "attended", "cancelled"].includes(status);
  
  // Check if appointment has recommended tasks
  const hasRecommendedTasks = item.recommendedTasks && item.recommendedTasks.length > 0;
  
  return (
    <>
      <div className="flex items-center gap-2">
        <div
          className={cn(
            "flex flex-row gap-[2px] items-center rounded-[888px] px-[6px] py-[4px] w-fit",
            TAG_STYLES[status as EncounterTagStatusType]
          )}
          data-testid="meeting-status"
        >
          {status === "completed" ? (
            <Calendar className={cn("size-4", TAG_STYLES[status as EncounterTagStatusType])} />
          ) : (
            <Clock className={cn("size-4", TAG_STYLES[status as EncounterTagStatusType])} />
          )}
          <span className="text-xs font-medium">{tag}</span>
        </div>
        {hasStartedOrPassed && (
          <>
            {hasRecommendedTasks ? (
              <div
                className={cn(
                  "flex flex-row gap-[2px] items-center rounded-[888px] px-[6px] py-[4px] w-fit",
                  "text-[#7B238D] bg-[#7B238D26] border"
                )}
                data-testid="consultation-status"
              >
                <Clock className="size-4 text-[#7B238D]" />
                <span className="text-xs font-medium">{"Resolved"}</span>
              </div>
            ) : (
              <div
                className={cn(
                  "flex flex-row gap-[2px] items-center rounded-[888px] px-[6px] py-[4px] w-fit",
                  "text-[#FFA726] bg-[#FFF5E6]"
                )}
                data-testid="consultation-status"
              >
                <Clock className="size-4 text-[#FFA726]" />
                <span className="text-xs font-medium">{"Action needed"}</span>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

const ConsultationStatus = ({ tag, program }: { tag: string, program: string }) => (
  <div className="flex gap-2 ">
    <div
      className={cn(
        "flex flex-row gap-[2px] items-center rounded-[888px] px-[6px] py-[4px] w-fit",
        "text-[#7B238D] bg-[#7B238D26] border"
      )}
      data-testid="consultation-status"
    >
      {program === "urgent-care" ? (
        <>
          <Stethoscope className="size-4 text-[#7B238D]" />
          <span className="text-xs font-medium">
            Consultation
          </span>
        </>
      ) : (
        <>
          <Clock className="size-4 text-[#7B238D]" />
          <span className="text-xs font-medium">
            {tag}
          </span>
        </>
      )}
    </div>
    {program === "urgent-care" && (
      <div
        className={cn(
          "flex flex-row gap-[2px] items-center rounded-[888px] px-[6px] py-[4px] w-fit",
          "text-[#FFA726] bg-[#FFF5E6]"
        )}
        data-testid="consultation-status"
      >
        <Info className="size-4 text-[#FFA726]" />
        <span className="text-xs font-medium">
          Action needed
        </span>
      </div>
    )}
  </div>
);

const InfoRow = ({ Icon, alt, value, testId }: {
  Icon: LucideIcon | string,
  alt: string,
  value: string,
  testId: string,
  isRefill?: boolean
}) => (
  <div className={cn("flex flex-row gap-2 items-center")} data-testid={testId}>
    {typeof Icon === "string" ? (
      <Image className={cn("w-3 h-3 sm:w-4 sm:h-4 text-[#7F8493]")} src={Icon} aria-label={alt} layout="constrained" width={16} height={16} />
    ) : (
      <Icon className={cn("w-3 h-3 sm:w-4 sm:h-4 text-[#7F8493] font-bold")} aria-label={alt} />
    )}
    <span className={cn("text-xs sm:text-sm font-normal text-[#7F8493] whitespace-normal break-words")}>{value}</span>
  </div>
);

const EncounterAppointmentsCard = (props: UpcomingEncounterCardProps) => {
  const { item } = props;
  const meetingInfo = [
    { Icon: "/icons/meeting-now.svg", alt: "calendar", value: item.date || "No time specified", testId: "meeting-date" },
  ];
  return (
    <div className="flex flex-col gap-2">
      {meetingInfo.map((info) => (
        <InfoRow key={info.testId} {...info} />
      ))}
      {props.program === "urgent-care" && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center text-sm font-normal text-[#202020CC] gap-2 flex-wrap">
            {item.patient.age !== undefined && (
              <>
                <span>{item.patient.age} years old</span>
              </>
            )}
            {item.patient.gender && (
              <>
                <span className="mx-2">•</span>
                <span>{item.patient.gender}</span>
              </>
            )}
          </div>
          <p className="text-sm font-normal text-[#202020CC]">
            <span className="font-semibold">Diagnosis:</span> {item.session?.diagnosis?.[0]?.disease_name ?? "No diagnosis available"}
          </p>
        </div>
      )}
    </div>
  );
};

const EncounterRefillsCard = (props: UpcomingEncounterCardProps) => {
  const { item } = props;
  const meetingInfo = [
    {
      Icon: "/icons/meeting-now.svg",
      alt: "calendar",
      value: item.date || "No time specified",
      testId: "consultation-date"
    },
    ...(() => {
      const validRefills = item.medicalRefills?.filter(r => r?.name || r?.dosage) ?? [];
      return validRefills.length > 0
        ? validRefills.map((refill, index) => ({
          Icon: "/icons/prescription.svg",
          alt: "medication-refills",
          value: `${refill.name || ""} ${refill.dosage || ""}`.trim() || "--",
          testId: `medication-refills-${index}`
        }))
        : [{
          Icon: "/icons/prescription.svg",
          alt: "medication-refills",
          value: "--",
          testId: "medication-refills-empty"
        }];
    })()
  ];
  return (
    <div className="flex flex-col gap-2 h-fit">
      {meetingInfo.map((info) => (
        <InfoRow key={info.testId} {...info} />
      ))}
    </div>
  );
};

const EncounterConsultationsCard = (props: UpcomingEncounterCardProps) => {
  const { item, program } = props;
  const timeAgo = formatTimeAgo(item.instanceCreated);
  const diseaseName = item.session?.diagnosis?.length
    ? item.session?.diagnosis[0]?.disease_name || "No diagnosis available"
    : "No diagnosis available";

  const age = item.patient?.age;
  const gender = item.patient?.gender;
  const meetingInfo = [
    {
      Icon: "/icons/meeting-now.svg",
      alt: "calendar",
      value: item.date || "No time specified",
      testId: "consultation-date"
    },
    // {
    //   Icon: "/icons/weightloss-program-icon.svg",
    //   alt: "consultation",
    //   value: "Weightloss program",
    //   testId: "consultation"
    // },
  ];
  return program === "urgent-care" ? (
    <div className="flex flex-col gap-2">
      <div className="flex items-center text-sm font-normal text-[#202020CC] gap-2 flex-wrap">
        <span>{timeAgo}</span>
        {age !== undefined && (
          <>
            <span className="mx-2">•</span>
            <span>{age} years old</span>
          </>
        )}
        {gender && (
          <>
            <span className="mx-2">•</span>
            <span>{gender}</span>
          </>
        )}
      </div>
      <p className="text-sm font-normal text-[#1A202C]">
        <span className="font-semibold">Diagnosis:</span>{" "}
        {diseaseName}
      </p>
    </div>
  ) : (
    <div className="flex flex-col md:flex-row gap-2 h-fit">
      {meetingInfo.map((info) => (
        <InfoRow key={info.testId} {...info} />
      ))}
    </div>
  );
};

const UpcomingEncounterCard = (props: UpcomingEncounterCardProps) => {
  const { item, program } = props;
  console.log("encounter_id", item)
  const routeApi = getRouteApi("/(dashboard)/dashboard/$program");
  const navigate = routeApi.useNavigate();
  const search = routeApi.useSearch() as { encounter_id?: string };
  const encounter_id = search.encounter_id;

  const [selectedEncounter, setSelectedEncounter] = useState<FormattedEncounter | null>(null);
  const [isAppointmentOpen, setAppointmentOpen] = useState(false);
  const [isConsultationOpen, setIsConsultationOpen] = useState(false);
  const [isReviewCardOpen, setIsReviewCardOpen] = useState(false);

  // Check if this encounter matches the encounter_id in URL params
  useEffect(() => {
    if (encounter_id === item.id) {
      if (item.type === "appointment") {
        setAppointmentOpen(true);
      } else if (item.type === "consultation") {
        setIsConsultationOpen(true);
      } else if (item.type === "refill") {
        setIsReviewCardOpen(true);
      }
      setSelectedEncounter(item);
    }
  }, [encounter_id, item.id, item.type, item]);

  const cardColor = item.type === "appointment" ? CARD_COLORS.appointment : item.type === "consultation" ? CARD_COLORS.consultation : CARD_COLORS.medication;

  const handleClick = () => {
    if (!item.id) return;
    navigate({
      search: { ...search, encounter_id: item.id },
    });
  };

  const handleAppointmentClose = (open: boolean) => {
    setAppointmentOpen(open);
    if (!open) {
      navigate({
        search: { ...search, encounter_id: undefined },
      });
    }
  };

  const handleConsultationClose = (open: boolean) => {
    setIsConsultationOpen(open);
    if (!open) {
      navigate({
        search: { ...search, encounter_id: undefined },
      });
    }
  };

  const handleReviewCardClose = (open: boolean) => {
    setIsReviewCardOpen(open);
    if (!open) {
      navigate({
        search: { ...search, encounter_id: undefined },
      });
    }
  };

  // Check for medication_payment tasks to determine resolved/action needed status
  const normalizedTasks = Array.isArray(item.recommendedTasks) ? item.recommendedTasks : [];
  const medicationPaymentTasks = normalizedTasks.filter(
    (task: any) => task?.type === "medication_payment"
  );

  // There may be multiple medication_payment tasks.
  const anyCompletedPayment = medicationPaymentTasks.some(
    (task: any) => task?.status === "completed"
  );
  const anyNotCompletedPayment = medicationPaymentTasks.some(
    (task: any) => task?.status !== "completed"
  );

  // Find the first completed task for erx check
  const completedPaymentTask = medicationPaymentTasks.find(
    (task: any) => task?.status === "completed"
  );

  // showResolvedTag only if ALL are completed and is_erx_sent is true for the completed one.
  // showActionNeededTag if ANY are incomplete, OR if all are completed but is_erx_sent is NOT true for the completed one.
  const showResolvedTag = completedPaymentTask?.is_erx_sent === true && !anyNotCompletedPayment;
  const showActionNeededTag =
    (anyNotCompletedPayment && anyCompletedPayment) || // some completed, some not
    (anyCompletedPayment && !anyNotCompletedPayment && completedPaymentTask?.is_erx_sent !== true); // all completed, but is_erx_sent is NOT true
  const showInProgressTag =
    !anyCompletedPayment && anyNotCompletedPayment;
  return (
    <>
      <div className="w-full flex h-fit">
        <div
          className={cn(`w-[6px] h-auto`, cardColor)}
          role="presentation"
        />
        <div
          className={cn(
            "w-full flex justify-between items-start p-4 border border-[#E6E6E6] rounded-tr-[8px] rounded-br-[8px] shadow-sm bg-white cursor-pointer transition-colors h-fit",
          )}
          onClick={() => handleClick()}
        >
          <div className="flex flex-col gap-2">
            {item.type === "appointment" && <AppointmentStatus status={item.status} tag={item.tag} item={item} />}
            {item.type === "consultation" && (
              <div className="flex gap-2 items-center">
                <ConsultationStatus tag="Consultation" program={program} />
                {showResolvedTag && (
                  <span className="text-xs font-medium py-[6px] px-[6px] rounded-full text-[#16A34A] bg-[#ECFDF3] w-fit flex items-center gap-1">
                    <CircleCheck className="size-[16px] text-[#16A34A]" />
                    Resolved
                  </span>
                )}
                {showActionNeededTag && (
                  <span className="text-xs font-medium py-[4px] px-[6px] rounded-full text-[#FFA726] bg-[#FFF5E6] w-fit flex items-center gap-1">
                    <Info className="size-[16px] text-[#FFA726]" />
                    Action needed
                  </span>
                )}
                {showInProgressTag && (
                  <span className="text-xs font-medium py-[6px] px-[8px] rounded-full text-[#2563EB] bg-[#EFF6FF] w-fit flex items-center gap-1">
                    <Clock className="size-[16px] text-[#2563EB]" />
                    In progress
                  </span>
                )}
              </div>
            )}
            {item.type === "refill" && program === "urgent-care" && (
              <div className="flex gap-2 items-center">
                {/* Refill tag */}
                <span className="text-xs font-medium py-[6px] px-[6px] rounded-full text-[#16A34A] bg-[#ECFDF3] w-fit flex items-center gap-1">
                  <Pill className="w-3.5 h-3.5 text-[#16A34A]" />
                  Refill
                </span>
                {/* Action needed tag */}
                <span className="text-xs font-medium py-[4px] px-[6px] rounded-full text-[#FFA726] bg-[#FFF5E6] w-fit flex items-center gap-1">
                  <Info className="size-[16px] text-[#FFA726]" />
                  Action needed
                </span>
              </div>
            )}
            <div className="flex gap-2 md:items-center items-start">
              <h2 className="text-base font-medium text-[#202020CC] whitespace-normal">{item.title}</h2>
              {program === "urgent-care" ? (
                item.patient.urgent_care_onboarded && item.patient.identity_verified && (
                  <div className="">
                    <Image src="/icons/check.svg" alt="timeline" layout="constrained" className="w-4 h-4 sm:w-5 sm:h-5" width={96} height={96} />
                  </div>
                )
              ) : (
                item.patient.medical_plan?.payment_status === "paid" && item.patient.glp1_guide_accepted && item.patient.identity_verified && item.patient.supplementary_health_assessment && Object.keys(item.patient.supplementary_health_assessment).length > 0 && (
                  <div className="">
                    <Image src="/icons/check.svg" alt="timeline" layout="constrained" className="w-4 h-4 sm:w-5 sm:h-5" width={96} height={96} />
                  </div>
                )
              )}
            </div>
            {item.type === "appointment" && <EncounterAppointmentsCard {...props} />}
            {item.type === "refill" && <EncounterRefillsCard {...props} />}
            {item.type === "consultation" && <EncounterConsultationsCard {...props} />}
          </div>
        </div>
      </div>
      {isAppointmentOpen && selectedEncounter && (
        <AppointmentModal
          open={isAppointmentOpen}
          onOpenChange={handleAppointmentClose}
          encounter={selectedEncounter}
          type={item.type}
        />
      )}
      {isConsultationOpen && selectedEncounter && (
        <ConsultationModal
          open={isConsultationOpen}
          onOpenChange={handleConsultationClose}
          encounter={selectedEncounter}
        />
      )}
      {isReviewCardOpen && selectedEncounter && selectedEncounter?.id && (
        <MedicalRefillsSheet
          open={isReviewCardOpen}
          onOpenChange={handleReviewCardClose}
          refillId={selectedEncounter.id}
          userDosespotId={selectedEncounter?.patient?.user_dosespot_id}
          sessionId={selectedEncounter?.id}
          encounterId={selectedEncounter?.encounterId}
          urgent_care_onboarded={selectedEncounter?.patient?.urgent_care_onboarded}
          recommendedTasks={selectedEncounter.recommendedTasks}
        />
      )}
    </>
  );
};

export default UpcomingEncounterCard;